import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Receipt, Plus, Search, Eye, Bell, Pencil, Trash2, X, ExternalLink, Printer, FileText, Check, Download, Loader2, Link2,
} from "lucide-react";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";
import AdminLayout from "../components/layout/AdminLayout";
import { useInvoiceStore, type Invoice, type InvoiceFilters } from "../store/useInvoiceStore";
import { useAuthStore } from "../store/useAuthStore";
import { useSearchParams } from "react-router-dom";
import InvoiceFormDrawer from "../components/invoices/InvoiceFormDrawer";
import { API_BASE_URL, DOWNLOAD_BASE_URL } from "../lib/api";

const fmtMoney = (n: number, cur = "USD") => `${cur === "USD" ? "$" : cur + " "}${Math.round(n || 0).toLocaleString()}`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const statusStyle = (s: string) => ({
  Upcoming: "bg-slate-100 text-slate-600 ring-slate-200",
  Submitted: "bg-blue-50 text-blue-700 ring-blue-200",
  Escrowed: "bg-amber-50 text-amber-700 ring-amber-200",
  Released: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  Completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Overdue: "bg-rose-50 text-rose-700 ring-rose-200",
}[s] || "bg-slate-100 text-slate-600 ring-slate-200");

const KPI_DEFS = [
  { key: "total", label: "Total Invoices", tone: "text-slate-900" },
  { key: "upcoming", label: "Upcoming", tone: "text-slate-600" },
  { key: "submitted", label: "Submitted", tone: "text-blue-700" },
  { key: "escrowed", label: "Escrowed", tone: "text-amber-700" },
  { key: "released", label: "Released", tone: "text-indigo-700" },
  { key: "completed", label: "Completed", tone: "text-emerald-700" },
  { key: "overdue", label: "Overdue", tone: "text-rose-700" },
] as const;

export default function AdminInvoicesPage() {
  const navigate = useNavigate();
  const { invoices, kpis, statuses, loading, fetchInvoices, fetchKpis, fetchMeta, deleteInvoice, sendReminder, markPaid } = useInvoiceStore();
  const { authUser }: any = useAuthStore();
  // Invoices are a Finance / Sales-Director workspace. Finance User is read-only.
  const canWrite = ["Admin", "Supervisor", "Sales Director"].includes(authUser?.role);

  const [filters, setFilters] = useState<InvoiceFilters>({});
  // Deep link from a milestone's "View Invoice" (?search=INV-1024).
  const [searchParams] = useSearchParams();
  const initialSearchFromUrl = searchParams.get("search") || "";
  useEffect(() => { if (initialSearchFromUrl) setF("search", initialSearchFromUrl); /* eslint-disable-next-line */ }, []);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [viewing, setViewing] = useState<Invoice | null>(null);
  const [reminderFor, setReminderFor] = useState<Invoice | null>(null);
  const [deleteFor, setDeleteFor] = useState<Invoice | null>(null);

  // Client filter options derived from the loaded invoices (no /clients call —
  // that endpoint is blocked for the Finance User role).
  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    invoices.forEach((inv) => { if (inv.client) map.set(inv.client._id, inv.client.name); });
    return [...map.entries()].map(([_id, name]) => ({ _id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [invoices]);

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchInvoices(filters);
      fetchKpis(filters);
    }, 250);
    return () => clearTimeout(t);
  }, [filters, fetchInvoices, fetchKpis]);

  const setF = (k: keyof InvoiceFilters, v: string) => setFilters((p) => ({ ...p, [k]: v || undefined }));
  const refresh = () => { fetchInvoices(filters); fetchKpis(filters); };

  // Live-refresh when a payment is confirmed (webhook → socket).
  useEffect(() => {
    const onSync = () => { fetchInvoices(filters); fetchKpis(filters); };
    window.addEventListener("cre:invoice-sync", onSync);
    return () => window.removeEventListener("cre:invoice-sync", onSync);
  }, [filters, fetchInvoices, fetchKpis]);

  // CSV export — respects the currently applied filters. Downloads via a blob so
  // we get loading feedback + error handling (rather than a naked link).
  const [exporting, setExporting] = useState<"" | "invoices">("");
  const exportCsv = async (kind: "invoices") => {
    setExporting(kind);
    try {
      const res = await axiosInstance.get("/invoices/export", { params: filters, responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success("Invoices exported");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting("");
    }
  };

  const totalValue = kpis?.totalValue || 0;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 max-w-[1500px] mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Receipt size={22} className="text-[#0058c3]" /> Invoice Management
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">Billing across Upwork &amp; Direct projects — KPIs, milestone invoices, reminders &amp; collections.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => exportCsv("invoices")} disabled={!!exporting} title="Export filtered invoices to CSV"
              className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-slate-200 rounded-lg text-[13px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {exporting === "invoices" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Export CSV
            </button>
            {canWrite && (
              <button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0058c3] text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 hover:bg-[#004b9e]">
                <Plus size={18} /> New Invoice
              </button>
            )}
          </div>
        </div>

        {/* Financial Overview — KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {KPI_DEFS.map((k) => (
            <div key={k.key} className="bg-white rounded-xl border border-slate-200 shadow-sm p-3.5">
              <div className={`text-2xl font-extrabold ${k.tone} tabular-nums`}>{(kpis as any)?.[k.key] ?? 0}</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mt-0.5">{k.label}</div>
            </div>
          ))}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 shadow-sm p-3.5">
            <div className="text-2xl font-extrabold text-[#0058c3] tabular-nums">{fmtMoney(totalValue)}</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mt-0.5">Total Value</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 relative min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={filters.search || ""} onChange={(e) => setF("search", e.target.value)} placeholder="Search invoice #…" className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <input type="date" value={filters.from || ""} onChange={(e) => setF("from", e.target.value)} title="From" className={fInp} />
          <input type="date" value={filters.to || ""} onChange={(e) => setF("to", e.target.value)} title="To" className={fInp} />
          <select value={filters.projectType || ""} onChange={(e) => setF("projectType", e.target.value)} className={fInp}>
            <option value="">Type: All</option><option value="Upwork">Upwork</option><option value="Direct">Direct</option>
          </select>
          <select value={filters.status || ""} onChange={(e) => setF("status", e.target.value)} className={fInp}>
            <option value="">Status: All</option>{statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.client || ""} onChange={(e) => setF("client", e.target.value)} className={fInp}>
            <option value="">Contact: All</option>{clientOptions.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          {Object.keys(filters).length > 0 && (
            <button onClick={() => setFilters({})} className="text-[12px] text-slate-500 hover:text-slate-800 font-semibold">Clear</button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px] min-w-[980px]">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  {["Invoice #", "Project", "Contact", "Type", "Milestone", "Amount", "Payment", "Due", "Status", "Updated", ""].map((h) => (
                    <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && invoices.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-14 text-center text-slate-400">Loading…</td></tr>
                ) : invoices.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-14 text-center text-slate-400">No invoices match the current filters.</td></tr>
                ) : (
                  invoices.map((inv) => (
                    <tr key={inv._id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 font-bold text-slate-900 whitespace-nowrap">{inv.invoiceNumber}
                        {inv.source === "auto" && <span className="ml-1.5 text-[9px] font-bold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Auto</span>}
                      </td>
                      <td className="px-4 py-3">
                        {inv.project ? (
                          <button onClick={() => navigate(`/admin/projects?open=${inv.project!._id}`)} className="text-[#0058c3] font-semibold hover:underline inline-flex items-center gap-1">
                            {inv.project.name} <ExternalLink size={11} />
                          </button>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {inv.client ? (
                          <button onClick={() => navigate(`/admin/clients?open=${inv.client!._id}`)} className="text-[#0058c3] font-semibold hover:underline inline-flex items-center gap-1">
                            {inv.client.name} <ExternalLink size={11} />
                          </button>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{inv.projectSource}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{inv.milestoneTitle || "—"}</td>
                      <td className="px-4 py-3 font-bold text-slate-900 font-mono whitespace-nowrap">{fmtMoney(inv.amount, inv.currency)}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{inv.paymentMethod || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(inv.dueDate)}</td>
                      <td className="px-4 py-3"><span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ring-1 ${statusStyle(inv.status)}`}>{inv.status}</span></td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-[12px]">{fmtDate(inv.updatedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-0.5">
                          <IconBtn title="View" onClick={() => setViewing(inv)}><Eye size={15} /></IconBtn>
                          {(inv as any).paymentToken && (
                            <IconBtn title="Copy payment link" onClick={() => {
                              navigator.clipboard.writeText((inv as any).payUrl || `${window.location.origin}/pay/${(inv as any).paymentToken}`);
                              toast.success("Payment link copied");
                            }}><Link2 size={15} /></IconBtn>
                          )}
                          {canWrite && <>
                            <IconBtn title="Send Reminder" onClick={() => setReminderFor(inv)}><Bell size={15} /></IconBtn>
                            <IconBtn title="Edit" onClick={() => { setEditing(inv); setFormOpen(true); }}><Pencil size={15} /></IconBtn>
                            <IconBtn title="Delete" danger onClick={() => setDeleteFor(inv)}><Trash2 size={15} /></IconBtn>
                          </>}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {formOpen && <InvoiceFormDrawer editing={editing} onClose={() => setFormOpen(false)} onSaved={refresh} />}
      {viewing && <InvoiceViewDrawer invoice={viewing} canWrite={canWrite} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); setFormOpen(true); }} onMarkPaid={async () => { const ok = await markPaid(viewing._id); if (ok) setViewing(null); }} />}
      {reminderFor && <ReminderModal invoice={reminderFor} onClose={() => setReminderFor(null)} onSend={sendReminder} />}
      {deleteFor && <DeleteModal invoice={deleteFor} onClose={() => setDeleteFor(null)} onConfirm={async () => { const ok = await deleteInvoice(deleteFor._id); if (ok) setDeleteFor(null); }} />}
    </AdminLayout>
  );
}

const fInp = "px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500";

const IconBtn = ({ title, onClick, danger, children }: { title: string; onClick: () => void; danger?: boolean; children: React.ReactNode }) => (
  <button onClick={onClick} title={title} className={`p-1.5 rounded-lg hover:bg-slate-100 ${danger ? "text-slate-400 hover:text-rose-600" : "text-slate-500 hover:text-blue-600"}`}>{children}</button>
);

// ---- View drawer ----
function InvoiceViewDrawer({ invoice, canWrite, onClose, onEdit, onMarkPaid }: { invoice: Invoice; canWrite: boolean; onClose: () => void; onEdit: () => void; onMarkPaid: () => void }) {
  const navigate = useNavigate();
  const attHref = (a: any) => a.r2Key ? `${API_BASE_URL}/invoices/${invoice._id}/attachments/${a._id}/download` : (/^https?:\/\//.test(a.url) ? a.url : `${DOWNLOAD_BASE_URL.replace(/\/$/, "")}/${a.url.replace(/^\//, "")}`);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <aside onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase font-bold text-slate-400">{invoice.invoiceType}</p>
            <h2 className="text-lg font-extrabold text-slate-900">{invoice.invoiceNumber}</h2>
            <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ring-1 ${statusStyle(invoice.status)}`}>{invoice.status}</span>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-[13px]">
          <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
            <div className="text-[11px] font-semibold text-blue-700 uppercase">Amount</div>
            <div className="text-2xl font-extrabold text-blue-900">{fmtMoney(invoice.amount, invoice.currency)}</div>
          </div>
          <Row label="Contact" value={invoice.client?.name} onClick={invoice.client ? () => navigate(`/admin/clients?open=${invoice.client!._id}`) : undefined} />
          <Row label="Project" value={invoice.project?.name} onClick={invoice.project ? () => navigate(`/admin/projects?open=${invoice.project!._id}`) : undefined} />
          <Row label="Milestone" value={invoice.milestoneTitle || "—"} />
          <Row label="Payment Method" value={invoice.paymentMethod || "—"} />
          <Row label="Payment Terms" value={invoice.paymentTerms || "—"} />
          <Row label="Invoice Date" value={fmtDate(invoice.invoiceDate)} />
          <Row label="Due Date" value={fmtDate(invoice.dueDate)} />
          <Row label="Contact Email" value={invoice.contactEmail || invoice.client?.email || "—"} />

          {invoice.lineItems && invoice.lineItems.length > 0 && (
            <div>
              <h3 className="text-[11px] font-bold uppercase text-slate-500 mb-2">Line Items</h3>
              <div className="space-y-1">
                {invoice.lineItems.map((li, i) => (
                  <div key={i} className="flex justify-between text-[12px] bg-slate-50 rounded px-3 py-2">
                    <span className="text-slate-700">{li.description || "—"} <span className="text-slate-400">×{li.quantity}</span></span>
                    <span className="font-mono font-semibold">{Math.round((li.quantity * li.unitPrice - li.discount) * (1 + li.tax / 100)).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {invoice.attachments && invoice.attachments.length > 0 && (
            <div>
              <h3 className="text-[11px] font-bold uppercase text-slate-500 mb-2">Attachments</h3>
              <div className="space-y-1">
                {invoice.attachments.map((a) => (
                  <a key={a._id} href={attHref(a)} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded border border-slate-100 px-3 py-2 hover:bg-slate-50">
                    <span className="flex items-center gap-2 min-w-0 text-slate-800"><FileText size={14} className="text-blue-600 shrink-0" /><span className="truncate">{a.name}</span></span>
                    <span className="text-[10px] uppercase text-slate-400">{a.category}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-[11px] font-bold uppercase text-slate-500 mb-2">Reminder History</h3>
            {invoice.reminders && invoice.reminders.length > 0 ? (
              <div className="space-y-1">
                {invoice.reminders.map((r, i) => (
                  <div key={i} className="text-[12px] text-slate-600 bg-slate-50 rounded px-3 py-1.5">
                    <b>{r.recipient}</b> · {fmtDate(r.sentAt)}{r.note ? ` · ${r.note}` : ""}
                  </div>
                ))}
              </div>
            ) : <p className="text-[12px] text-slate-400">No reminders sent yet.</p>}
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50/50 flex gap-2">
          <a href={`${API_BASE_URL}/invoices/${invoice._id}/print`} target="_blank" rel="noreferrer" className="flex-1 px-3 py-2 text-[13px] font-semibold border border-slate-200 rounded-lg text-slate-700 hover:bg-white inline-flex items-center justify-center gap-1.5"><Printer size={14} /> Print / PDF</a>
          {canWrite && invoice.status !== "Completed" && (
            <button onClick={onMarkPaid} className="px-3 py-2 text-[13px] font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 inline-flex items-center justify-center gap-1.5"><Check size={14} /> Mark Paid</button>
          )}
          {canWrite && (
            <button onClick={onEdit} className="flex-1 px-3 py-2 text-[13px] font-semibold bg-[#0058c3] text-white rounded-lg hover:bg-[#004b9e] inline-flex items-center justify-center gap-1.5"><Pencil size={14} /> Edit</button>
          )}
        </div>
      </aside>
    </div>
  );
}

const Row = ({ label, value, onClick }: { label: string; value?: string | undefined; onClick?: (() => void) | undefined }) => (
  <div className="flex justify-between items-center py-1 border-b border-slate-50">
    <span className="text-slate-500">{label}</span>
    {onClick ? (
      <button onClick={onClick} className="text-[#0058c3] font-semibold hover:underline inline-flex items-center gap-1">{value || "—"} <ExternalLink size={11} /></button>
    ) : <span className="font-semibold text-slate-800">{value || "—"}</span>}
  </div>
);

// ---- Reminder modal ----
function ReminderModal({ invoice, onClose, onSend }: { invoice: Invoice; onClose: () => void; onSend: (id: string, r: string, n?: string) => Promise<boolean> }) {
  const [recipient, setRecipient] = useState("Client");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => { setSending(true); const ok = await onSend(invoice._id, recipient, note); setSending(false); if (ok) onClose(); };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-900">Send Reminder — {invoice.invoiceNumber}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Recipient</label>
            <div className="grid grid-cols-3 gap-2">
              {["Client", "Business Developer", "Both"].map((r) => (
                <button key={r} onClick={() => setRecipient(r)} className={`px-2 py-2 text-[12px] font-semibold rounded-lg border ${recipient === r ? "bg-[#0058c3] text-white border-[#0058c3]" : "bg-white border-slate-200 text-slate-600"}`}>{r === "Business Developer" ? "BD" : r}</button>
              ))}
            </div>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note…" className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={send} disabled={sending} className="px-4 py-2 text-sm font-semibold bg-[#0058c3] text-white rounded-lg hover:bg-[#004b9e] disabled:opacity-50 inline-flex items-center gap-1.5"><Bell size={14} /> {sending ? "Sending…" : "Send"}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Delete (two-step) ----
function DeleteModal({ invoice, onClose, onConfirm }: { invoice: Invoice; onClose: () => void; onConfirm: () => void }) {
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState("");
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5">
          <h3 className="text-base font-bold text-rose-700">Delete Invoice?</h3>
          {step === 1 ? (
            <>
              <p className="text-[13px] text-slate-600 mt-2">Invoice <b>{invoice.invoiceNumber}</b> will be removed. This action cannot be undone.</p>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={() => setStep(2)} className="px-4 py-2 text-sm font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700">Continue</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px] text-slate-600 mt-2">Type <b className="font-mono">DELETE</b> to confirm permanent deletion.</p>
              <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" className="w-full mt-3 px-3 py-2 text-[13px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500" />
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={onConfirm} disabled={typed !== "DELETE"} className="px-4 py-2 text-sm font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-40">Delete permanently</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
